import { useCallback, useEffect, useState } from 'react';
import { CheckCircle2, AlertTriangle, Unlock } from 'lucide-react';
import { api } from '../../lib/api';
import { Money, fmtDate, usd, parseCents, centsToInput, inputCls, btnCls, btn2Cls, cardCls, thCls, tdCls } from './shared';

// Monthly bank tie-out: the book balance at month end must equal the ending balance on the
// Relay statement. A tied month is locked so its bank lines cannot drift afterwards.

function lastMonth() {
  const d = new Date(); d.setDate(1); d.setMonth(d.getMonth() - 1);
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}`;
}

export default function TieOutTab({ me, accounts }) {
  const cash = accounts.filter((a) => a.is_cash);
  const [accountId, setAccountId] = useState(cash[0]?.id || '');
  const [period, setPeriod] = useState(lastMonth());
  const [view, setView] = useState(null);
  const [stmt, setStmt] = useState('');
  const [note, setNote] = useState('');
  const [error, setError] = useState('');
  const [busy, setBusy] = useState(false);

  const load = useCallback(async () => {
    if (!/^\d{4}-\d{2}$/.test(period)) return;
    try {
      const v = await api.get(`/books/tie-out/${period}?account_id=${accountId}`);
      setView(v); setStmt(v.saved ? centsToInput(v.saved.statement_ending_cents) : ''); setNote(v.saved?.note || ''); setError('');
    } catch (e) { setError(e.message); setView(null); }
  }, [period, accountId]);
  useEffect(() => { load(); }, [load]);

  const stmtCents = parseCents(stmt, { allowNegative: true });
  const diff = view && stmtCents != null ? view.book_ending_cents - stmtCents : null;
  const save = async () => {
    setBusy(true);
    try { setView(await api.post(`/books/tie-out/${period}`, { account_id: accountId, statement_ending_cents: stmtCents, note: note || null })); setError(''); } catch (e) { setError(e.message); } finally { setBusy(false); }
  };
  const reopen = async () => {
    if (!window.confirm(`Reopen ${period}? Its bank lines become editable again.`)) return;
    try { setView(await api.post(`/books/tie-out/${period}/reopen`, { account_id: accountId })); } catch (e) { setError(e.message); }
  };

  return (
    <div className="space-y-4">
      <div className="flex flex-wrap items-center gap-2">
        <select className={inputCls} value={accountId} onChange={(e) => setAccountId(e.target.value)} aria-label="Account">
          {cash.map((a) => <option key={a.id} value={a.id}>{a.code} {a.name}</option>)}
        </select>
        <input type="month" className={inputCls} value={period} onChange={(e) => setPeriod(e.target.value)} aria-label="Month" />
      </div>
      {error && <p className="text-sm text-rose-600">{error}</p>}
      {view && (
        <>
          <div className={`${cardCls} grid grid-cols-2 gap-3 text-sm sm:grid-cols-5`}>
            <div><div className="text-xs text-[var(--text-secondary)]">Opening</div><Money cents={view.opening_cents} /></div>
            <div><div className="text-xs text-[var(--text-secondary)]">Money in</div><Money cents={view.money_in_cents} /></div>
            <div><div className="text-xs text-[var(--text-secondary)]">Money out</div><Money cents={-view.money_out_cents} /></div>
            <div><div className="text-xs text-[var(--text-secondary)]">Books at month end</div><Money cents={view.book_ending_cents} className="font-semibold" /></div>
            <div>
              <div className="text-xs text-[var(--text-secondary)]">Statement ending balance</div>
              <input className={`${inputCls} w-32 text-right`} value={stmt} onChange={(e) => setStmt(e.target.value)} disabled={!me.can_edit} inputMode="decimal" placeholder="0.00" />
            </div>
          </div>
          <div className="flex flex-wrap items-center gap-3">
            {view.saved?.tied ? (
              <span className="flex items-center gap-1 text-sm font-medium text-emerald-700 dark:text-emerald-400"><CheckCircle2 size={16} /> Tied and locked</span>
            ) : diff === 0 ? (
              <span className="text-sm text-emerald-700 dark:text-emerald-400">Matches. Save to lock the month.</span>
            ) : diff != null ? (
              <span className="flex items-center gap-1 text-sm text-rose-600"><AlertTriangle size={16} /> Books are {diff > 0 ? 'higher' : 'lower'} than the statement by {usd(Math.abs(diff))}</span>
            ) : <span className="text-sm text-[var(--text-secondary)]">Enter the ending balance from the Relay statement.</span>}
            {view.uncategorized_in_period > 0 && <span className="text-xs text-amber-700 dark:text-amber-400">{view.uncategorized_in_period} line(s) this month still need a category (they count toward the balance).</span>}
          </div>
          {me.can_edit && (
            <div className="flex flex-wrap items-center gap-2">
              <input className={`${inputCls} min-w-0 flex-1 sm:max-w-md`} placeholder="Note (optional)" value={note} onChange={(e) => setNote(e.target.value)} />
              <button className={btnCls} disabled={stmtCents == null || busy || view.saved?.tied} onClick={save}>Save tie-out</button>
              {view.saved?.tied && <button className={btn2Cls} onClick={reopen}><Unlock size={14} /> Reopen month</button>}
            </div>
          )}
          <div className="overflow-x-auto rounded-xl border border-[var(--border)] bg-white dark:bg-slate-900">
            <table className="w-full min-w-[560px] text-sm">
              <thead className="bg-gray-50 dark:bg-slate-800"><tr><th className={thCls}>Date</th><th className={thCls}>Payee / memo</th><th className={`${thCls} text-right`}>Amount</th></tr></thead>
              <tbody>
                {view.entries.slice().reverse().map((e) => {
                  const l = e.lines.find((x) => x.account_id === view.account.id);
                  const m = (view.q2c_matches || []).find((x) => x.entry_id === e.id);
                  return (
                    <tr key={e.id} className="border-t border-[var(--border)]">
                      <td className={`${tdCls} whitespace-nowrap`}>{fmtDate(e.entry_date)}</td>
                      <td className={tdCls}>{e.counterparty || e.memo}{e.uncategorized && <span className="ml-2 text-xs text-amber-700">to categorize</span>}
                        {m && <span className="ml-2 text-xs text-sky-700 dark:text-sky-400" title="Same amount within a few days of a quote-to-cash payment. The deal counts the payment, not this deposit. Confirm it is the same money.">matched: {m.invoice_number} payment ({m.deal_name}), confirm</span>}</td>
                      <td className={`${tdCls} text-right`}><Money cents={l ? l.debit_cents - l.credit_cents : 0} /></td>
                    </tr>
                  );
                })}
                {view.entries.length === 0 && <tr><td className={tdCls} colSpan={3}>No lines this month.</td></tr>}
              </tbody>
            </table>
          </div>
          {view.history.length > 0 && (
            <div>
              <h3 className="mb-1 text-sm font-semibold text-[var(--text-primary)]">Saved tie-outs</h3>
              <ul className="text-xs text-[var(--text-secondary)]">
                {view.history.map((h) => <li key={h.id}>{h.period}: {h.tied ? 'tied' : `off by ${usd(h.difference_cents)}`} (statement {usd(h.statement_ending_cents)}){h.note ? `, ${h.note}` : ''}</li>)}
              </ul>
            </div>
          )}
        </>
      )}
    </div>
  );
}
